class HorizontalCarousel extends HTMLElement {
  constructor() {
    super();

    // Element references
    this.carousel = null;
    this.leftButton = null;
    this.rightButton = null;
    this.progressBar = null;
    
    // Configuration from attributes
    this.scrollAmount = 0;
    this.carouselSelector = this.getAttribute('carousel-selector') || '.overflow-x-scroll';
    this.leftButtonSelector = 'button[data-carousel-dir="left"]';
    this.rightButtonSelector = 'button[data-carousel-dir="right"]';
    this.enableProgressBar = this.getAttribute('enable-progress-bar') === 'true';
    // Disable the arrow at either end instead of hiding it
    this.disableButtons = this.getAttribute('disable-buttons') === 'true';
    // Let a mouse drag the track (touch already scrolls natively)
    this.enableDrag = this.getAttribute('enable-drag') === 'true';

    // Drag state
    this.dragStartX = 0;
    this.dragStartScroll = 0;
    this.isPointerDown = false;
    this.hasDragged = false;

    // Bound methods for event listeners (for cleanup)
    this.handleClick = this.handleClick.bind(this);
    this.updateProgress = this.updateProgress.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  initialize() {
    // Find required elements
    this.carousel = this.querySelector(this.carouselSelector);
    if (!this.carousel) {
      console.warn(`ProgressCarousel: Carousel element not found with selector "${this.carouselSelector}"`);
      return;
    }

    this.leftButton = this.querySelector(this.leftButtonSelector);
    this.rightButton = this.querySelector(this.rightButtonSelector);
    
    // Find progress bar if enabled
    if (this.enableProgressBar) {
      this.progressBar = this.querySelector('[data-progress-bar]');
    }

    // Attach event listeners
    this.addEventListener('click', this.handleClick);
    
    // Add scroll listener for progress bar
    if (this.enableProgressBar) {
      this.carousel.addEventListener('scroll', this.updateProgress);
      // Set initial progress
      this.updateProgress();
    }

    if (this.enableDrag) {
      this.carousel.addEventListener('pointerdown', this.onPointerDown);
      this.carousel.addEventListener('click', this.onClickCapture, true);
      this.carousel.addEventListener('dragstart', this.onDragStart);
    }
  }

  calculateScrollAmount() {
    const firstItem = this.carousel.querySelector('[data-carousel-item]');
    if (!firstItem) {
      this.scrollAmount = 150;
      return;
    }

    const firstItemStyle = window.getComputedStyle(firstItem);
    const firstItemWidth = firstItem.offsetWidth;
    const firstItemMarginRight = parseFloat(firstItemStyle.marginRight) || 0;
    this.scrollAmount = firstItemWidth + firstItemMarginRight;
  }

  cleanup() {
    if (this.carousel && this.enableProgressBar) {
      this.carousel.removeEventListener('scroll', this.updateProgress);
    }

    if (this.carousel && this.enableDrag) {
      this.carousel.removeEventListener('pointerdown', this.onPointerDown);
      this.carousel.removeEventListener('click', this.onClickCapture, true);
      this.carousel.removeEventListener('dragstart', this.onDragStart);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
    }

    this.removeEventListener('click', this.handleClick);
  }

  onPointerDown(e) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    if (this.carousel.scrollWidth <= this.carousel.clientWidth) return;

    this.isPointerDown = true;
    this.hasDragged = false;
    this.dragStartX = e.clientX;
    this.dragStartScroll = this.carousel.scrollLeft;
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
  }

  onPointerMove(e) {
    if (!this.isPointerDown) return;
    const delta = e.clientX - this.dragStartX;

    // A few pixels of slack so a plain click on a card still navigates
    if (!this.hasDragged) {
      if (Math.abs(delta) < 5) return;
      this.hasDragged = true;
      // Snapping and smooth scrolling fight a 1:1 drag, so pause them until release
      this.carousel.style.scrollSnapType = 'none';
      this.carousel.style.scrollBehavior = 'auto';
      this.carousel.style.cursor = 'grabbing';
      this.carousel.style.userSelect = 'none';
    }

    this.carousel.scrollLeft = this.dragStartScroll - delta;
  }

  onPointerUp() {
    this.isPointerDown = false;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    if (!this.hasDragged) return;

    this.carousel.style.cursor = '';
    this.carousel.style.userSelect = '';
    this.carousel.style.scrollBehavior = '';

    // Settle on the nearest card, then hand control back to CSS snapping
    const items = this.carousel.querySelectorAll('[data-carousel-item]');
    const step = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : 0;
    if (step > 0) {
      this.carousel.scrollTo({
        left: Math.round(this.carousel.scrollLeft / step) * step,
        behavior: 'smooth',
      });
    }
    setTimeout(() => {
      this.carousel.style.scrollSnapType = '';
    }, 400);
  }

  // Swallow the click that ends a drag so it doesn't open the product under the cursor
  onClickCapture(e) {
    if (!this.hasDragged) return;
    e.preventDefault();
    e.stopPropagation();
    this.hasDragged = false;
  }

  // Native image/link dragging would hijack the pointer
  onDragStart(e) {
    e.preventDefault();
  }

  handleClick(e) {
    if (!this.carousel) return;

    if (!this.scrollAmount) this.calculateScrollAmount();

    if (e.target.getAttribute('data-carousel-dir') === 'left') {
      this.carousel.scrollLeft = this.carousel.scrollLeft - this.scrollAmount;
      if (this.enableProgressBar) {
        this.updateProgress();
      }
    } else if (e.target.getAttribute('data-carousel-dir') === 'right') {
      this.carousel.scrollLeft = this.carousel.scrollLeft + this.scrollAmount;
      if (this.enableProgressBar) {
        this.updateProgress();
      }
    }
  }

  updateProgress() {
    if (!this.carousel) return;
    
    const maxScroll = this.carousel.scrollWidth - this.carousel.clientWidth;
    let scrollPercentage = maxScroll > 0 ? (this.carousel.scrollLeft / maxScroll) * 100 : 0;

    // Update button state
    if (this.disableButtons) {
      // Allow a pixel of rounding at either end
      if (this.leftButton) this.leftButton.disabled = this.carousel.scrollLeft <= 1;
      if (this.rightButton) this.rightButton.disabled = this.carousel.scrollLeft >= maxScroll - 1;
    } else {
      if (this.leftButton) {
        if (scrollPercentage == 0) {
          this.leftButton.classList.add('hidden');
        } else {
          this.leftButton.classList.remove('hidden');
        }
      }
      if (this.rightButton) {
        if (scrollPercentage == 100) {
          this.rightButton.classList.add('hidden');
        } else {
          this.rightButton.classList.remove('hidden');
        }
      }
    }

    // Update progress bar width (minimum 25%)
    if (this.progressBar) {
      if (scrollPercentage < 25) scrollPercentage = 25;
      this.progressBar.style.width = `${scrollPercentage}%`;
    }
  }

  // Public method to scroll to a specific position
  scrollTo(position) {
    if (this.carousel) {
      this.carousel.scrollLeft = position;
    }
  }
}

// Register the custom element
if (!customElements.get('horizontal-carousel')) {
  customElements.define('horizontal-carousel', HorizontalCarousel);
}
